import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { BrandLogoFull } from "../components/BrandLogo";
import api from "../lib/api";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("verifying"); // verifying, success, already_verified, error
  const [message, setMessage] = useState("");
  
  // Ref to prevent double API calls in React StrictMode
  const hasVerified = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token found. Please check the link in your email.");
      return;
    }

    // Prevent double verification in StrictMode
    if (hasVerified.current) {
      return;
    }
    hasVerified.current = true;

    const verifyEmail = async () => {
      try {
        const response = await api.get(`/auth/verify-email?token=${token}`);
        const data = response.data;
        if (data.status === "already_verified") {
          setStatus("already_verified");
        } else {
          setStatus("success");
        }
        setMessage(data.message);
      } catch (error) {
        // Check if this is a "token already used" error (double-click scenario)
        const detail = error.response?.data?.detail || "";
        if (detail.toLowerCase().includes("already verified") || error.response?.data?.status === "already_verified") {
          setStatus("already_verified");
          setMessage("Your email has already been verified. Please log in.");
        } else {
          setStatus("error");
          setMessage(error.response?.data?.detail || "Verification failed. The link may have expired.");
        }
      }
    };

    verifyEmail();
  }, [token]);

  const isSuccess = status === "success" || status === "already_verified";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="relative z-10 w-full max-w-md animate-in">
        <div className="flex justify-center mb-8">
          <BrandLogoFull className="h-24" />
        </div>

        <Card className="glass-card">
          <CardContent className="p-8 text-center">
            {status === "verifying" && (
              <>
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
                <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3" data-testid="verify-status-heading">
                  Verifying...
                </h2>
                <p className="text-muted-foreground" data-testid="verify-status-message">
                  Please wait while we verify your email address.
                </p>
              </>
            )}

            {isSuccess && (
              <>
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3" data-testid="verify-status-heading">
                  Email Verified
                </h2>
                <p className="text-muted-foreground mb-6" data-testid="verify-status-message">
                  {message}
                </p>
                <div className="p-4 bg-white/[0.03] rounded-xl mb-6">
                  <p className="text-sm text-muted-foreground font-bold">
                    Your account is ready! Log in and start adding clients.
                  </p>
                </div>
                <Link to="/">
                  <Button
                    className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                    data-testid="go-to-login-btn"
                  >
                    Go to Login
                  </Button>
                </Link>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3" data-testid="verify-status-heading">
                  Verification Failed
                </h2>
                <p className="text-muted-foreground mb-6" data-testid="verify-status-message">
                  {message}
                </p>
                <div className="flex flex-col gap-3">
                  <Link to="/sign-up">
                    <Button
                      className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                      data-testid="try-again-btn"
                    >
                      Sign Up Again
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button
                      variant="outline"
                      className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl border-primary/50 text-primary hover:bg-primary/10"
                      data-testid="back-to-login-btn"
                    >
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            Tracked with <span className="text-primary font-medium">FitTrack Coach</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <a href="https://app.fittrackcoach.in" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">app.fittrackcoach.in</a>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">by InfiNest Tech Pvt. Ltd.</p>
        </div>
      </div>
    </div>
  );
}
